/**
 * Utskriftsvennlig visning av prosjektkalkylen (materialliste + økonomi).
 * Bruker samme totalberegning som tilbud (src/domain/calc/totals.ts).
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { ArrowLeft, Printer } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  useCompanyProfile,
  formatOrgNumber,
  formatPhone,
  formatWebsite,
} from "@/hooks/use-company-profile";
import { formatDate } from "@/lib/format";
import { useCompanyVat } from "@/hooks/use-company-vat";
import { computeTotals, type CalcLine, type LineType } from "@/domain/calc/totals";

export const Route = createFileRoute("/_authenticated/projects/$id_/calc/pdf")({
  component: ProjectCalcPdfView,
});

const nok = new Intl.NumberFormat("nb-NO", {
  style: "currency",
  currency: "NOK",
  maximumFractionDigits: 0,
});
const num = new Intl.NumberFormat("nb-NO", { maximumFractionDigits: 2 });

const KNOWN_TYPES: LineType[] = [
  "material",
  "small_parts",
  "labor",
  "standard_job",
  "service_vehicle",
  "travel",
  "lift",
  "documentation",
  "discount",
];

const MATERIAL_TYPES = new Set(["material", "small_parts"]);
const LABOR_TYPES = new Set([
  "labor",
  "standard_job",
  "service_vehicle",
  "travel",
  "lift",
  "documentation",
]);

const TYPE_LABEL: Record<string, string> = {
  material: "Material",
  small_parts: "Smådeler",
  labor: "Timer",
  standard_job: "Standardjobb",
  service_vehicle: "Servicebil",
  travel: "Reise",
  lift: "Lift",
  documentation: "Dokumentasjon",
  discount: "Rabatt",
};

type LineRow = {
  id: string;
  name: string;
  line_type: string;
  quantity: number;
  unit: string;
  unit_cost: number;
  unit_price: number;
  sort_order: number;
};

function ProjectCalcPdfView() {
  const { id: projectId } = Route.useParams();
  const { data: company } = useCompanyProfile();
  const { vatPercent } = useCompanyVat();

  const q = useQuery({
    queryKey: ["project-calc-pdf", projectId],
    queryFn: async () => {
      const [projRes, linesRes] = await Promise.all([
        supabase
          .from("projects")
          .select(
            "id, name, project_number, address, postal_code, city, customers(name, address, postal_code, city)",
          )
          .eq("id", projectId)
          .maybeSingle(),
        supabase
          .from("project_lines")
          .select("id, name, line_type, quantity, unit, unit_cost, unit_price, sort_order")
          .eq("project_id", projectId)
          .order("sort_order"),
      ]);
      if (projRes.error) throw projRes.error;
      if (linesRes.error) throw linesRes.error;
      return {
        project: projRes.data as
          | (Record<string, unknown> & {
              name: string;
              project_number: string | null;
              address: string | null;
              postal_code: string | null;
              city: string | null;
              customers: { name: string; address?: string | null; postal_code?: string | null; city?: string | null } | null;
            })
          | null,
        lines: (linesRes.data ?? []) as LineRow[],
      };
    },
  });

  const ready = Boolean(q.data && company);
  useEffect(() => {
    if (ready) {
      const t = setTimeout(() => window.print(), 500);
      return () => clearTimeout(t);
    }
  }, [ready]);

  if (!q.data || !company) {
    return <div className="p-10 text-center text-muted-foreground">Laster…</div>;
  }

  const { project, lines } = q.data;
  const calcLines: CalcLine[] = lines
    .filter((l) => (KNOWN_TYPES as string[]).includes(l.line_type))
    .map((l) => ({
      item_type: l.line_type as LineType,
      quantity: Number(l.quantity),
      unit_cost: Number(l.unit_cost),
      unit_price: Number(l.unit_price),
    }));
  const totals = computeTotals(calcLines, { discount_percent: 0, vat_percent: vatPercent ?? 0 });

  const materials = lines.filter((l) => MATERIAL_TYPES.has(l.line_type));
  const labor = lines.filter((l) => LABOR_TYPES.has(l.line_type));
  const other = lines.filter(
    (l) => !MATERIAL_TYPES.has(l.line_type) && !LABOR_TYPES.has(l.line_type),
  );

  const accent = company.pdf_accent_color?.trim() || "#111827";
  const footerText = `${company.company_name} • Org.nr ${formatOrgNumber(company.org_number)} • ${formatWebsite(company.website)}`;
  const siteAddress = [project?.address, [project?.postal_code, project?.city].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ");

  return (
    <>
      <style>{`
        @page {
          size: A4;
          margin: 22mm 18mm 26mm 18mm;
          @bottom-center {
            content: "${footerText.replace(/"/g, '\\"')} — Side " counter(page) " av " counter(pages);
            font-size: 9pt;
            color: #6b7280;
          }
        }
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .no-print { display: none !important; }
          .avoid-break { break-inside: avoid; page-break-inside: avoid; }
        }
      `}</style>

      <div className="min-h-screen bg-neutral-100 print:bg-white">
        <div className="no-print sticky top-0 z-10 border-b border-border bg-card/95 backdrop-blur">
          <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-3">
            <Link to="/projects/$id" params={{ id: projectId }} className="btn btn-ghost">
              <ArrowLeft className="h-4 w-4" /> Tilbake
            </Link>
            <button onClick={() => window.print()} className="btn btn-primary">
              <Printer className="h-4 w-4" /> Skriv ut / lagre PDF
            </button>
          </div>
        </div>

        <article className="mx-auto my-8 max-w-[210mm] bg-white px-14 py-12 text-neutral-900 shadow-xl print:my-0 print:max-w-none print:p-0 print:shadow-none">
          <header
            className="flex items-start justify-between gap-8 border-b-2 pb-8"
            style={{ borderColor: accent }}
          >
            <div>
              <div className="text-lg font-semibold tracking-tight">{company.company_name}</div>
              <div className="text-xs text-neutral-500">
                Org.nr {formatOrgNumber(company.org_number)}
              </div>
              <div className="text-xs text-neutral-500">
                {formatPhone(company.phone)} · {company.company_email}
              </div>
            </div>
            <div className="text-right">
              <div
                className="text-[11px] font-semibold uppercase tracking-[0.2em]"
                style={{ color: accent }}
              >
                Kalkyle · Internt dokument
              </div>
              <div className="text-[10px] font-medium uppercase tracking-wider text-neutral-500">
                Inneholder kost og margin – skal ikke sendes kunde
              </div>

              <div className="mt-1 font-mono text-sm text-neutral-700">
                {project?.project_number ?? ""}
              </div>
              <div className="mt-3 text-[11px] uppercase tracking-wider text-neutral-400">Dato</div>
              <div className="text-sm text-neutral-700">{formatDate(new Date().toISOString())}</div>
            </div>
          </header>

          <section className="mt-8 grid grid-cols-2 gap-8 text-sm avoid-break">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-neutral-400">Prosjekt</div>
              <div className="font-medium">{project?.name ?? "—"}</div>
              {siteAddress && <div className="text-neutral-600">{siteAddress}</div>}
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-neutral-400">Kunde</div>
              <div className="font-medium">{project?.customers?.name ?? "—"}</div>
              {project?.customers?.address && (
                <div className="text-neutral-600">
                  {project.customers.address}
                  {project.customers.postal_code || project.customers.city
                    ? `, ${[project.customers.postal_code, project.customers.city].filter(Boolean).join(" ")}`
                    : ""}
                </div>
              )}
            </div>
          </section>

          {lines.length === 0 ? (
            <p className="mt-10 text-sm text-neutral-500">
              Ingen linjer registrert på prosjektet.
            </p>
          ) : (
            <>
              <LineTable title="Materialliste" rows={materials} />
              <LineTable title="Timer og påslag" rows={labor} />
              <LineTable title="Øvrige linjer" rows={other} />

              <section className="mt-10 avoid-break">
                <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-neutral-500">
                  Økonomi
                </h2>
                <div className="ml-auto w-full max-w-sm text-sm">
                  <TotalRow label="Sum salg (eks. mva)" value={nok.format(totals.subtotal_before_discount)} />
                  <TotalRow label="Sum kost" value={nok.format(totals.cost)} />
                  <TotalRow label="Rabatt" value={nok.format(totals.discount_amount)} />
                  <TotalRow label="Netto (eks. mva)" value={nok.format(totals.subtotal)} />
                  <TotalRow label={`Mva ${vatPercent ?? 0} %`} value={nok.format(totals.vat)} />
                  <TotalRow label="Total (inkl. mva)" value={nok.format(totals.total)} strong />
                  <div className="mt-3 border-t pt-3">
                    <TotalRow label="Dekningsbidrag" value={nok.format(totals.margin)} strong />
                    <TotalRow label="DB %" value={`${totals.marginPct.toFixed(1)} %`} strong />
                  </div>
                </div>
              </section>
            </>
          )}
        </article>
      </div>
    </>
  );
}

function LineTable({ title, rows }: { title: string; rows: LineRow[] }) {
  if (rows.length === 0) return null;
  const sum = rows.reduce((s, r) => s + Number(r.quantity) * Number(r.unit_price), 0);
  return (
    <section className="mt-10 avoid-break">
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-neutral-500">
        {title}
      </h2>
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b text-left text-[11px] uppercase tracking-wider text-neutral-400">
            <th className="py-2">Beskrivelse</th>
            <th className="py-2">Type</th>
            <th className="py-2 text-right">Antall</th>
            <th className="py-2">Enhet</th>
            <th className="py-2 text-right">Enhetspris</th>
            <th className="py-2 text-right">Sum</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-neutral-100 align-top">
              <td className="py-2 pr-3">{r.name}</td>
              <td className="py-2 pr-3 text-neutral-500">{TYPE_LABEL[r.line_type] ?? r.line_type}</td>
              <td className="py-2 pr-3 text-right tabular-nums">{num.format(Number(r.quantity))}</td>
              <td className="py-2 pr-3 text-neutral-500">{r.unit}</td>
              <td className="py-2 pr-3 text-right tabular-nums">{nok.format(Number(r.unit_price))}</td>
              <td className="py-2 text-right tabular-nums">
                {nok.format(Number(r.quantity) * Number(r.unit_price))}
              </td>
            </tr>
          ))}
          <tr>
            <td colSpan={5} className="py-2 text-right text-xs uppercase tracking-wider text-neutral-400">
              Sum
            </td>
            <td className="py-2 text-right font-semibold tabular-nums">{nok.format(sum)}</td>
          </tr>
        </tbody>
      </table>
    </section>
  );
}

function TotalRow({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-neutral-600">{label}</span>
      <span className={`tabular-nums ${strong ? "font-semibold" : ""}`}>{value}</span>
    </div>
  );
}
